import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Loader2 } from "lucide-react";
import api from "@/utils/api";
import { toast } from "@/hooks/use-toast";
import {
  setData,
  setCurrentTab,
  setUserInitiatedNav,
} from "../../../store/slices/newLoomSlice";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import FilePreview from "../../../components/common/FilePreview";
import purchase_certificate from "../../../assets/documents/purchase_certificate.pdf";

const particularsSchema = (alreadyPurchased, finalMaxLooms) =>
  z
    .object({
      basic_cost_machinery: z.string().optional(),
      basic_cost_accessories: z
        .string()
        .optional()
        .refine((val) => !val || (!isNaN(Number(val)) && Number(val) >= 0), {
          message: "Basic cost must be a valid number",
        }),
      custom_duty: z
        .string()
        .optional()
        .refine((val) => !val || (!isNaN(Number(val)) && Number(val) >= 0), {
          message: "Custom duty must be a valid number",
        }),
      total_investment: z.string().optional(),
      total_loan_applied: z.string().optional(),
      total_loan_released: z.string().optional(),
      loan_release_date: z.string().optional(),
      loan_period: z.string().optional(),
      promoters_contribution: z.string().optional(),
      promoters_contribution_percent: z
        .string()
        .optional()
        .refine((val) => {
          if (!val) return true;
          const num = Number(val);
          return !isNaN(num) && num <= 100;
        }, "Promoter's contribution must not exceed 100%"),
      shared_with_other_banks: z.string().optional(),
      loan_sanction_date: z.string().optional(),
      loan_application_date: z.string().optional(),
      loan_sanction_by_authority_date: z.string().optional(),
      repayment_start_date: z.string().optional(),
      term_loan_account_number: z.string().optional(),
      interest_rate: z
        .string()
        .optional()
        .refine((val) => {
          if (!val) return true;
          const num = Number(val);
          return !isNaN(num) && num <= 100;
        }, "Interest Rate must not exceed 100%"),
      interest_charged_per_year: z.string().optional(),
      purchase_date: z.string().optional(),
      country_of_origin: z.string().optional(),
      bill_of_lading_date: z.string().optional(),
      bill_of_entry_date: z.string().optional(),
      commissioning_date: z.string().optional(),
      purchase_proof: z.any().optional(),
      origin_certificate: z.any().optional(),
      bill_of_lading: z.any().optional(),
      bill_of_entry: z.any().optional(),
      dpr_copy: z.any().optional(),
      purchase_detail_bond: z.any().optional(),
      investment_proof: z.any().optional(),
      loan_account_copy: z.any().optional(),
      amount_incurred: z.string().optional(),
      already_purchase: z.string().optional(),
      no_of_loom_to_purchase: z.string().optional(),
      estimated_total_subsidy_amount: z.string().optional(),
      loom_purchases: z
        .array(
          z.object({
            loom_name: z.string().optional(),
            serial_number: z.string().optional(),
            machinery_type: z.string().optional(),
            make_model: z.string().optional(),
            country_of_origin: z.string().optional(),
            purchase_date: z.string().optional(),
            invoice_copy: z.array(z.any()).optional(),
            machine_photo: z.array(z.any()).optional(),
          })
        )
        .optional(),
    })
    .superRefine((data, ctx) => {
      if (alreadyPurchased) {
        if (!data.already_purchase) {
          ctx.addIssue({
            path: ["already_purchase"],
            code: z.ZodIssueCode.custom,
            message: "This field is required",
          });
        }

        // if (!data.estimated_total_subsidy_amount) {
        //   ctx.addIssue({
        //     path: ["estimated_total_subsidy_amount"],
        //     code: z.ZodIssueCode.custom,
        //     message: "This field is required",
        //   });
        // }

        if (
          !data.no_of_loom_to_purchase ||
          data.no_of_loom_to_purchase.trim() === ""
        ) {
          ctx.addIssue({
            path: ["no_of_loom_to_purchase"],
            code: z.ZodIssueCode.custom,
            message: "Number of looms is required",
          });
        } else {
          const num = Number(data.no_of_loom_to_purchase);
          if (isNaN(num) || num < 1 || num > finalMaxLooms) {
            ctx.addIssue({
              path: ["no_of_loom_to_purchase"],
              code: z.ZodIssueCode.custom,
              message: `Number of looms must be between 1 and ${finalMaxLooms}`,
            });
          }
        }
      }

      if (!alreadyPurchased || data.already_purchase === "Yes") {
        const requiredFields = [
          {
            field: "basic_cost_machinery",
            message: "Basic cost of machinery is required",
          },
          {
            field: "basic_cost_accessories",
            message: "Basic Accessories cost of machinery is required",
          },
          {
            field: "promoters_contribution",
            message: "Applicant contribution is required",
          },
          {
            field: "promoters_contribution_percent",
            message: "Applicant contribution percent is required",
          },
          {
            field: "shared_with_other_banks",
            message: "This field is required",
          },
          {
            field: "amount_incurred",
            message: "Amount Incurred is required",
          },
          {
            field: "total_investment",
            message: "Total investment is required",
          },
        ];

        for (const { field, message } of requiredFields) {
          if (
            !data[field] ||
            (typeof data[field] === "string" && data[field].trim() === "")
          ) {
            ctx.addIssue({
              path: [field],
              code: z.ZodIssueCode.custom,
              message: message,
            });
          }
        }

        if (!data.purchase_proof) {
          ctx.addIssue({
            path: ["purchase_proof"],
            code: z.ZodIssueCode.custom,
            message: "The purchase proof field is required.",
          });
        }

        if (!data.investment_proof) {
          ctx.addIssue({
            path: ["investment_proof"],
            code: z.ZodIssueCode.custom,
            message: "The investment proof field is required.",
          });
        }

        if (
          data.basic_cost_machinery &&
          (isNaN(Number(data.basic_cost_machinery)) ||
            Number(data.basic_cost_machinery) < 0)
        ) {
          ctx.addIssue({
            path: ["basic_cost_machinery"],
            code: z.ZodIssueCode.custom,
            message: "Basic cost must be a valid number",
          });
        }

        data.loom_purchases?.forEach((loom, index) => {
          if (!loom.loom_name || loom.loom_name.trim() === "") {
            ctx.addIssue({
              path: ["loom_purchases", index, "loom_name"],
              code: z.ZodIssueCode.custom,
              message: "Loom name is required for each loom.",
            });
          }

          if (!loom.serial_number || loom.serial_number.trim() === "") {
            ctx.addIssue({
              path: ["loom_purchases", index, "serial_number"],
              code: z.ZodIssueCode.custom,
              message: "Serial number is required for each loom.",
            });
          }

          if (
            !loom.machinery_type ||
            (loom.machinery_type !== "Imported" &&
              loom.machinery_type !== "Indigenous")
          ) {
            ctx.addIssue({
              path: ["loom_purchases", index, "machinery_type"],
              code: z.ZodIssueCode.custom,
              message: "Machinery type is required for each loom.",
            });
          }

          if (!loom.make_model || loom.make_model.trim() === "") {
            ctx.addIssue({
              path: ["loom_purchases", index, "make_model"],
              code: z.ZodIssueCode.custom,
              message: "Make & Model is required for each loom.",
            });
          }

          if (!loom.purchase_date) {
            ctx.addIssue({
              path: ["loom_purchases", index, "purchase_date"],
              code: z.ZodIssueCode.custom,
              message: "Purchase date is required for each loom.",
            });
          }
          if (new Date(loom.purchase_date) < new Date("2025-09-02")) {
            ctx.addIssue({
              path: ["loom_purchases", index, "purchase_date"],
              code: z.ZodIssueCode.custom,
              message: "Date of purchase must be after 02.09.2025",
            });
          }

          if (
            loom.machinery_type === "Imported" &&
            (!loom.country_of_origin || loom.country_of_origin.trim() === "")
          ) {
            ctx.addIssue({
              path: ["loom_purchases", index, "country_of_origin"],
              code: z.ZodIssueCode.custom,
              message: "Country of Origin is required for imported machinery",
            });
          }

          if (
            !loom.invoice_copy ||
            !Array.isArray(loom.invoice_copy) ||
            loom.invoice_copy.length === 0
          ) {
            ctx.addIssue({
              path: ["loom_purchases", index, "invoice_copy"],
              code: z.ZodIssueCode.custom,
              message: "Invoice copy is required",
            });
          }

          if (
            !loom.machine_photo ||
            !Array.isArray(loom.machine_photo) ||
            loom.machine_photo.length === 0
          ) {
            ctx.addIssue({
              path: ["loom_purchases", index, "machine_photo"],
              code: z.ZodIssueCode.custom,
              message: "Machine photo is required",
            });
          }
        });
      }
    });

const NewPurchasedDetails = ({
  data,
  districts,
  communities,
  already_purchased,
}) => {
  const [filePreviews, setFilePreviews] = useState({});
  const { available_limit } = useSelector((state) => state.newLoom);
  const finalMaxLooms = Math.min(5, available_limit.limit);
  const location = useLocation();
  const nav = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [buttonType, setButtonType] = useState("submit");
  const dispatch = useDispatch();
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    control,
    formState: { errors },
    trigger,
  } = useForm({
    resolver: zodResolver(particularsSchema(already_purchased, finalMaxLooms)),
    defaultValues: {
      machinery_type: data?.purchase_details?.machinery_type || "",
      make_model: data?.purchase_details?.make_model || "",
      serial_number: data?.purchase_details?.serial_number || "",
      basic_cost_machinery: data?.purchase_details?.basic_cost_machinery || "",
      basic_cost_accessories:
        data?.purchase_details?.basic_cost_accessories || "",
      custom_duty: data?.purchase_details?.custom_duty || "",
      purchase_date: data?.purchase_details?.purchase_date
        ? new Date(data?.purchase_details?.purchase_date)
            .toISOString()
            .split("T")[0]
        : "",
      bill_of_lading_date: data?.purchase_details?.bill_of_lading_date
        ? new Date(data?.purchase_details?.bill_of_lading_date)
            .toISOString()
            .split("T")[0]
        : "",
      bill_of_entry_date: data?.purchase_details?.bill_of_entry_date
        ? new Date(data?.purchase_details?.bill_of_entry_date)
            .toISOString()
            .split("T")[0]
        : "",
      commissioning_date: data?.purchase_details?.commissioning_date
        ? new Date(data?.purchase_details?.commissioning_date)
            .toISOString()
            .split("T")[0]
        : "",
      country_of_origin: data?.purchase_details?.country_of_origin || "",
      total_investment: data?.purchase_details?.total_investment ?? "",
      total_loan_applied: data?.purchase_details?.total_loan_applied ?? "",
      total_loan_released: data?.purchase_details?.total_loan_released ?? "",
      loan_release_date: data?.purchase_details?.loan_release_date
        ? new Date(data?.purchase_details?.loan_release_date)
            .toISOString()
            .split("T")[0]
        : "",
      loan_period: data?.purchase_details?.loan_period ?? "",
      promoters_contribution:
        data?.purchase_details?.promoters_contribution ?? "",
      promoters_contribution_percent:
        data?.purchase_details?.promoters_contribution_percent ?? "",
      shared_with_other_banks:
        data?.purchase_details?.shared_with_other_banks ?? "",
      loan_sanction_date: data?.purchase_details?.loan_sanction_date
        ? new Date(data?.purchase_details?.loan_sanction_date)
            .toISOString()
            .split("T")[0]
        : "",
      loan_application_date: data?.purchase_details?.loan_application_date
        ? new Date(data?.purchase_details?.loan_application_date)
            .toISOString()
            .split("T")[0]
        : "",
      loan_sanction_by_authority_date: data?.purchase_details
        ?.loan_sanction_by_authority_date
        ? new Date(data?.purchase_details?.loan_sanction_by_authority_date)
            .toISOString()
            .split("T")[0]
        : "",
      repayment_start_date: data?.purchase_details?.repayment_start_date
        ? new Date(data?.purchase_details?.repayment_start_date)
            .toISOString()
            .split("T")[0]
        : "",
      term_loan_account_number:
        data?.purchase_details?.term_loan_account_number ?? "",
      interest_rate: data?.purchase_details?.interest_rate ?? "",
      interest_charged_per_year:
        data?.purchase_details?.interest_charged_per_year ?? "",
      amount_incurred: data?.purchase_details?.amount_incurred ?? "",
      already_purchase: data?.purchase_details?.already_purchase ?? "",
      no_of_loom_to_purchase:
        data?.purchase_details?.no_of_loom_to_purchase ?? "",
      estimated_total_subsidy_amount:
        data?.purchase_details?.estimated_total_subsidy_amount ?? "",
      bill_of_lading: data?.purchase_details?.bill_of_lading ?? undefined,
      bill_of_entry: data?.purchase_details?.bill_of_entry ?? undefined,
      dpr_copy: data?.purchase_details?.dpr_copy ?? undefined,
      purchase_detail_bond:
        data?.purchase_details?.purchase_detail_bond ?? undefined,
      investment_proof: data?.purchase_details?.investment_proof ?? undefined,
      loan_account_copy: data?.purchase_details?.loan_account_copy ?? undefined,
      purchase_proof: data?.purchase_details?.purchase_proof ?? undefined,
      loom_purchases: Array.isArray(data?.purchase_details?.loom_purchases)
        ? data.purchase_details.loom_purchases.map((item) => ({
            serial_number: item.serial_number || "",
            loom_name: item.loom_name || "",
            machinery_type: item.machinery_type || "",
            make_model: item.make_model || "",
            country_of_origin: item.country_of_origin || "",
            purchase_date: item.purchase_date
              ? new Date(item.purchase_date).toISOString().split("T")[0]
              : "",
            invoice_copy: item.invoice_copy
              ? Array.isArray(item.invoice_copy)
                ? item.invoice_copy
                : [item.invoice_copy]
              : [],
            machine_photo: item.machine_photo
              ? Array.isArray(item.machine_photo)
                ? item.machine_photo
                : [item.machine_photo]
              : [],
          }))
        : [],
    },
  });

  const formData = watch();
  const loomCount = watch("no_of_loom_to_purchase");

  useEffect(() => {
    if (data) {
      const previews = {
        bill_of_lading: data?.purchase_details?.bill_of_lading ?? undefined,
        bill_of_entry: data?.purchase_details?.bill_of_entry ?? undefined,
        dpr_copy: data?.purchase_details?.dpr_copy ?? undefined,
        purchase_detail_bond:
          data?.purchase_details?.purchase_detail_bond ?? undefined,
        investment_proof: data?.purchase_details?.investment_proof ?? undefined,
        loan_account_copy:
          data?.purchase_details?.loan_account_copy ?? undefined,
        purchase_proof: data?.purchase_details?.purchase_proof ?? undefined,
      };
      setFilePreviews(previews);
    }
  }, [data]);

  useEffect(() => {
    const count = Number(loomCount);
    if (!count || count < 1) return;

    const existingLooms = data?.purchase_details?.loom_purchases || [];

    setValue(
      "loom_purchases",
      Array.from({ length: count }).map((_, i) => {
        const existingLoom = existingLooms[i];

        return {
          loom_name: existingLoom?.loom_name || `Loom ${i + 1}`,
          serial_number: existingLoom?.serial_number || "",
          purchase_date: existingLoom?.purchase_date
            ? new Date(existingLoom.purchase_date).toISOString().split("T")[0]
            : "",
          machinery_type: existingLoom?.machinery_type || "",
          make_model: existingLoom?.make_model || "",
          country_of_origin: existingLoom?.country_of_origin || "",
          invoice_copy: existingLoom?.invoice_copy
            ? Array.isArray(existingLoom.invoice_copy)
              ? existingLoom.invoice_copy
              : [existingLoom.invoice_copy]
            : [],
          machine_photo: existingLoom?.machine_photo
            ? Array.isArray(existingLoom.machine_photo)
              ? existingLoom.machine_photo
              : [existingLoom.machine_photo]
            : [],
        };
      })
    );
  }, [loomCount, setValue, data?.purchase_details?.loom_purchases]);

  const handleFileUpload = async (name, file) => {
    if (file) {
      if (name === "machinery_photo" && file.type === "application/pdf") {
        toast({
          title: "Invalid File Type",
          description:
            "PDF files are not allowed for photo upload. Please upload an image file.",
          variant: "destructive",
        });
        return;
      }

      setValue(name, file, { shouldValidate: true });

      if (file.type.startsWith("image/")) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setFilePreviews((prev) => ({ ...prev, [name]: e.target.result }));
        };
        reader.readAsDataURL(file);
      }

      await trigger(name);
    }
  };

  const handleArrayFileUpload = async (fieldName, index, file) => {
    if (file) {
      const currentArray = formData.loom_purchases?.[index]?.[fieldName] || [];
      const newArray = [...currentArray, file];

      setValue(`loom_purchases.${index}.${fieldName}`, newArray, {
        shouldValidate: true,
      });

      await trigger(`loom_purchases.${index}.${fieldName}`);
    }
  };

  const handleRemoveArrayFile = (fieldName, index, fileIndex) => {
    const currentArray = formData.loom_purchases?.[index]?.[fieldName] || [];
    const newArray = currentArray.filter((_, i) => i !== fileIndex);

    setValue(`loom_purchases.${index}.${fieldName}`, newArray, {
      shouldValidate: true,
    });
  };

  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      const searchParams = new URLSearchParams(location?.search);
      const submitData = new FormData();

      if (formData.loom_purchases && Array.isArray(formData.loom_purchases)) {
        const validLoomPurchases = formData.loom_purchases.filter((loom) => {
          const hasSerialNumber =
            loom.serial_number && loom.serial_number.trim() !== "";
          const hasMachineryType =
            loom.machinery_type && loom.machinery_type.trim() !== "";
          const hasMakeModel = loom.make_model && loom.make_model.trim() !== "";
          const hasPurchaseDate =
            loom.purchase_date && loom.purchase_date.trim() !== "";
          const hasInvoice =
            loom.invoice_copy &&
            Array.isArray(loom.invoice_copy) &&
            loom.invoice_copy.length > 0;
          const hasPhoto =
            loom.machine_photo &&
            Array.isArray(loom.machine_photo) &&
            loom.machine_photo.length > 0;

          return (
            hasSerialNumber ||
            hasMachineryType ||
            hasMakeModel ||
            hasPurchaseDate ||
            hasInvoice ||
            hasPhoto
          );
        });

        validLoomPurchases.forEach((loom, loomIndex) => {
          submitData.append(
            `loom_purchases[${loomIndex}][loom_name]`,
            loom.loom_name
          );
          submitData.append(
            `loom_purchases[${loomIndex}][serial_number]`,
            loom.serial_number
          );
          submitData.append(
            `loom_purchases[${loomIndex}][machinery_type]`,
            loom.machinery_type
          );
          submitData.append(
            `loom_purchases[${loomIndex}][make_model]`,
            loom.make_model
          );
          submitData.append(
            `loom_purchases[${loomIndex}][country_of_origin]`,
            loom.country_of_origin || ""
          );
          submitData.append(
            `loom_purchases[${loomIndex}][purchase_date]`,
            loom.purchase_date
          );

          if (loom.invoice_copy && Array.isArray(loom.invoice_copy)) {
            loom.invoice_copy.forEach((file, fileIndex) => {
              if (file instanceof File) {
                submitData.append(
                  `loom_purchases[${loomIndex}][invoice_copy][${fileIndex}]`,
                  file
                );
              } else if (typeof file === "string") {
                submitData.append(
                  `loom_purchases[${loomIndex}][invoice_copy][${fileIndex}]`,
                  file
                );
              }
            });
          }

          if (loom.machine_photo && Array.isArray(loom.machine_photo)) {
            loom.machine_photo.forEach((file, fileIndex) => {
              if (file instanceof File) {
                submitData.append(
                  `loom_purchases[${loomIndex}][machine_photo][${fileIndex}]`,
                  file
                );
              } else if (typeof file === "string") {
                submitData.append(
                  `loom_purchases[${loomIndex}][machine_photo][${fileIndex}]`,
                  file
                );
              }
            });
          }
        });
      }

      Object.keys(formData).forEach((key) => {
        if (key === "loom_purchases") return;

        if (formData[key] !== undefined && formData[key] !== null) {
          if (formData[key] instanceof File) {
            submitData.append(key, formData[key]);
          } else {
            submitData.append(key, formData[key].toString());
          }
        }
      });

      submitData.append("id", data?.id || "");

      const url = already_purchased
        ? "/new_shuttleless/purchase_detail"
        : "/new_shuttleless/after_purchase_detail";

      const response = await api.post(url, submitData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      await dispatch(setData(response?.data?.data || {}));

      toast({
        title: "Success!",
        description: "Application Saved successfully",
        variant: "success",
      });

      searchParams.delete("tab");
      nav(
        {
          pathname: location.pathname,
          search: searchParams.toString(),
        },
        { replace: true }
      );

      dispatch(setUserInitiatedNav(true));

      if (buttonType === "next") {
        if (!already_purchased) {
          dispatch(setCurrentTab("purchase_declaration"));
        } else {
          dispatch(setCurrentTab("declaration"));
        }
      }
    } catch (error) {
      console.error("Submission error:", error);
      toast({
        title: "Submission Failed",
        description:
          error.response?.data?.message || "Failed to submit application",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = async (fieldName, value) => {
    setValue(fieldName, value, { shouldValidate: true });
  };

  const handleRemoveFile = (fieldName) => {
    setValue(fieldName, undefined, { shouldValidate: true });
    setFilePreviews((prev) => ({ ...prev, [fieldName]: null }));
  };

  const handleDownload = (fileUrl, fileName) => {
    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = fileName;
    link.click();
  };

  const onSubmitClick = async (e, type) => {
    e.preventDefault();
    setButtonType(type); // Save button type first
    console.log("🖱️ Submit button clicked. Type:", type);

    const isValid = await trigger();
    console.log("✅ Validation result:", isValid);
    console.log("⚠️ Validation errors:", errors);

    if (isValid) {
      console.log("🎯 Form is valid, calling handleSubmitSpv...");
      // await form.handleSubmit(handleSubmitSpv)();
    } else {
      toast({
        title: "Validation Error",
        description: "Please fix the errors before submitting",
        variant: "destructive",
      });
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Purchase Details
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6 mt-5">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
            {already_purchased && (
              <>
                <div className="space-y-2">
                  <Label htmlFor="no_of_loom_to_purchase">
                    Number of Looms to be Purchased / Replaced{" "}
                    <span className="text-red-500">*</span>
                  </Label>
                  <Input
                    id="no_of_loom_to_purchase"
                    {...register("no_of_loom_to_purchase")}
                    onChange={(e) => {
                      let value = e.target.value.replace(/\D/g, "").slice(0, 1);

                      if (value === "0" || value === "") {
                        handleInputChange("no_of_loom_to_purchase", "");
                        return;
                      }

                      const num = Number(value);

                      if (num <= finalMaxLooms) {
                        handleInputChange("no_of_loom_to_purchase", value);
                      }
                    }}
                    className={
                      errors.no_of_loom_to_purchase ? "border-red-500" : ""
                    }
                  />
                  {errors.no_of_loom_to_purchase && (
                    <p className="text-sm text-red-500">
                      {errors.no_of_loom_to_purchase.message}
                    </p>
                  )}
                  <p
                    className="ms-1 text-[12px] text-muted-foreground"
                    style={{ fontSize: "18px" }}
                  >
                    Max <b>{finalMaxLooms} Looms</b> Allowed
                  </p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="already_purchase">
                    Already Purchased <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.already_purchase || ""}
                    onValueChange={(value) =>
                      handleInputChange("already_purchase", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Yes or No" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.already_purchase && (
                    <p className="text-sm text-red-500">
                      {errors.already_purchase.message}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>

          <Separator />

          {(!already_purchased ||
            (formData.already_purchase &&
              formData.already_purchase === "Yes")) && (
            <>
              <div className="my-7 rounded-lg overflow-hidden">
                <h3 className="text-xl font-bold mb-4">
                  Date of Purchase of New Rapier Loom{" "}
                  <span className="text-red-500">*</span>
                </h3>

                {loomCount > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full overflow-x-auto mt-5 text-sm border">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-1 py-3 text-left border">
                            Loom Name *
                          </th>
                          <th className="px-1 py-3 text-left border">
                            Serial Number *
                          </th>
                          <th className="px-1 py-3 text-left border">
                            Purchase Date *
                          </th>
                          <th className="px-1 py-3 text-left border">
                            Machinery Type *
                          </th>
                          <th className="px-1 py-3 text-left border">
                            Make & Model *
                          </th>
                          <th className="px-1 py-3 text-left border">
                            Country of Origin if Imported
                          </th>
                          <th className="px-1 py-3 text-left border">
                            Machine Photo *
                          </th>
                          <th className="px-1 py-3 text-left border">
                            Invoice Copy *
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {formData.loom_purchases?.map((loom, index) => (
                          <tr key={index}>
                            <td className="px-1 w-[100px] py-3 border">
                              <Input
                                {...register(
                                  `loom_purchases.${index}.loom_name`
                                )}
                                className={
                                  errors?.loom_purchases?.[index]?.loom_name
                                    ? "border-red-500"
                                    : ""
                                }
                              />
                              {errors?.loom_purchases?.[index]?.loom_name && (
                                <p className="text-xs text-red-500 mt-1">
                                  {
                                    errors.loom_purchases[index].loom_name
                                      .message
                                  }
                                </p>
                              )}
                            </td>
                            <td className="px-1 py-3 border">
                              <Input
                                placeholder="Enter serial number"
                                {...register(
                                  `loom_purchases.${index}.serial_number`
                                )}
                                className={
                                  errors?.loom_purchases?.[index]?.serial_number
                                    ? "border-red-500"
                                    : ""
                                }
                              />
                              {errors?.loom_purchases?.[index]
                                ?.serial_number && (
                                <p className="text-xs text-red-500 mt-1">
                                  {
                                    errors.loom_purchases[index].serial_number
                                      .message
                                  }
                                </p>
                              )}
                            </td>
                            <td className="px-1 py-3 border">
                              <Input
                                type="date"
                                min="2025-09-02"
                                {...register(
                                  `loom_purchases.${index}.purchase_date`
                                )}
                                className={
                                  errors?.loom_purchases?.[index]?.purchase_date
                                    ? "border-red-500"
                                    : ""
                                }
                              />
                              {errors?.loom_purchases?.[index]
                                ?.purchase_date && (
                                <p className="text-xs text-red-500 mt-1">
                                  {
                                    errors.loom_purchases[index].purchase_date
                                      .message
                                  }
                                </p>
                              )}
                            </td>
                            <td className="px-1 py-3 border">
                              <Select
                                value={
                                  formData.loom_purchases[index]
                                    ?.machinery_type || ""
                                }
                                onValueChange={(value) =>
                                  setValue(
                                    `loom_purchases.${index}.machinery_type`,
                                    value,
                                    {
                                      shouldValidate: true,
                                    }
                                  )
                                }
                              >
                                <SelectTrigger
                                  className={
                                    errors?.loom_purchases?.[index]
                                      ?.machinery_type
                                      ? "border-red-500"
                                      : ""
                                  }
                                >
                                  <SelectValue placeholder="Select" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="Imported">
                                    Imported
                                  </SelectItem>
                                  <SelectItem value="Indigenous">
                                    Indigenous
                                  </SelectItem>
                                </SelectContent>
                              </Select>
                              {errors?.loom_purchases?.[index]
                                ?.machinery_type && (
                                <p className="text-xs text-red-500 mt-1">
                                  {
                                    errors.loom_purchases[index].machinery_type
                                      .message
                                  }
                                </p>
                              )}
                            </td>
                            <td className="px-1 py-3 border">
                              <Input
                                placeholder="Enter Make & Model"
                                {...register(
                                  `loom_purchases.${index}.make_model`
                                )}
                                className={
                                  errors?.loom_purchases?.[index]?.make_model
                                    ? "border-red-500"
                                    : ""
                                }
                              />
                              {errors?.loom_purchases?.[index]?.make_model && (
                                <p className="text-xs text-red-500 mt-1">
                                  {
                                    errors.loom_purchases[index].make_model
                                      .message
                                  }
                                </p>
                              )}
                            </td>
                            <td className="px-1 py-3 border">
                              <Input
                                placeholder="Enter Country"
                                {...register(
                                  `loom_purchases.${index}.country_of_origin`
                                )}
                                disabled={
                                  formData.loom_purchases[index]
                                    ?.machinery_type !== "Imported"
                                }
                                className={
                                  errors?.loom_purchases?.[index]
                                    ?.country_of_origin
                                    ? "border-red-500"
                                    : ""
                                }
                              />
                              {errors?.loom_purchases?.[index]
                                ?.country_of_origin && (
                                <p className="text-xs text-red-500 mt-1">
                                  {
                                    errors.loom_purchases[index]
                                      .country_of_origin.message
                                  }
                                </p>
                              )}
                            </td>
                            <td className="px-1 py-3 border">
                              <div className="space-y-2">
                                <Input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleArrayFileUpload(
                                        "machine_photo",
                                        index,
                                        file
                                      );
                                    }
                                  }}
                                  className={
                                    errors?.loom_purchases?.[index]
                                      ?.machine_photo
                                      ? "border-red-500"
                                      : ""
                                  }
                                />
                                {/* Display uploaded files with FilePreview */}
                                <div className="space-y-2">
                                  {formData.loom_purchases?.[
                                    index
                                  ]?.machine_photo?.map((file, fileIndex) => (
                                    <div
                                      key={fileIndex}
                                      className="border rounded p-2"
                                    >
                                      <FilePreview
                                        fieldName={`machine_photo_${index}_${fileIndex}`}
                                        preview={null}
                                        file={
                                          file instanceof File ? file : null
                                        }
                                        existingFile={
                                          typeof file === "string" ? file : null
                                        }
                                        onRemove={() =>
                                          handleRemoveArrayFile(
                                            "machine_photo",
                                            index,
                                            fileIndex
                                          )
                                        }
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                              {errors?.loom_purchases?.[index]
                                ?.machine_photo && (
                                <p className="text-xs text-red-500 mt-1">
                                  {
                                    errors.loom_purchases[index].machine_photo
                                      .message
                                  }
                                </p>
                              )}
                            </td>
                            <td className="px-1 py-3 border">
                              <div className="space-y-2">
                                <Input
                                  type="file"
                                  accept=".pdf,.jpg,.jpeg,.png"
                                  onChange={(e) => {
                                    const file = e.target.files?.[0];
                                    if (file) {
                                      handleArrayFileUpload(
                                        "invoice_copy",
                                        index,
                                        file
                                      );
                                    }
                                  }}
                                  className={
                                    errors?.loom_purchases?.[index]
                                      ?.invoice_copy
                                      ? "border-red-500"
                                      : ""
                                  }
                                />
                                {/* Display uploaded files with FilePreview */}
                                <div className="space-y-2">
                                  {formData.loom_purchases?.[
                                    index
                                  ]?.invoice_copy?.map((file, fileIndex) => (
                                    <div
                                      key={fileIndex}
                                      className="border rounded p-2"
                                    >
                                      <FilePreview
                                        fieldName={`invoice_copy_${index}_${fileIndex}`}
                                        preview={null}
                                        file={
                                          file instanceof File ? file : null
                                        }
                                        existingFile={
                                          typeof file === "string" ? file : null
                                        }
                                        onRemove={() =>
                                          handleRemoveArrayFile(
                                            "invoice_copy",
                                            index,
                                            fileIndex
                                          )
                                        }
                                      />
                                    </div>
                                  ))}
                                </div>
                              </div>
                              {errors?.loom_purchases?.[index]
                                ?.invoice_copy && (
                                <p className="text-xs text-red-500 mt-1">
                                  {
                                    errors.loom_purchases[index].invoice_copy
                                      .message
                                  }
                                </p>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="p-5 bg-blue-50 rounded-lg">
                    <h5 className="text-primary">
                      !!! Please Enter the Number of Rapier looms proposed to be
                      purchased. !!!
                    </h5>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="amount_incurred" required>
                    Amount Incurred
                  </Label>
                  <Input
                    id="amount_incurred"
                    type="text"
                    {...register("amount_incurred")}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      handleInputChange("amount_incurred", value);
                    }}
                  />
                  {errors.amount_incurred && (
                    <p className="text-sm text-red-500">
                      {errors.amount_incurred.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="basic_cost_machinery" required>
                    Basic Cost of Machinery (₹){" "}
                  </Label>
                  <Input
                    id="basic_cost_machinery"
                    {...register("basic_cost_machinery")}
                    className={
                      errors.basic_cost_machinery ? "border-red-500" : ""
                    }
                    placeholder="Enter amount"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />
                  {errors.basic_cost_machinery && (
                    <p className="text-sm text-red-500">
                      {errors.basic_cost_machinery.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="basic_cost_accessories" required>
                    Basic Cost of Accessories (₹)
                  </Label>
                  <Input
                    id="basic_cost_accessories"
                    {...register("basic_cost_accessories")}
                    placeholder="Enter amount"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="custom_duty">Custom Duty Paid (₹)</Label>
                  <Input
                    id="custom_duty"
                    {...register("custom_duty")}
                    placeholder="Enter amount"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bill_of_lading_date">
                    Bill of Lading Date
                  </Label>
                  <Input
                    id="bill_of_lading_date"
                    type="date"
                    {...register("bill_of_lading_date")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bill_of_entry_date">Bill of Entry Date</Label>
                  <Input
                    id="bill_of_entry_date"
                    type="date"
                    {...register("bill_of_entry_date")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="commissioning_date">Commissioning Date</Label>
                  <Input
                    id="commissioning_date"
                    type="date"
                    {...register("commissioning_date")}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="total_investment" required>
                    Total Investment (₹)
                  </Label>
                  <Input
                    id="total_investment"
                    {...register("total_investment")}
                    className={errors.total_investment ? "border-red-500" : ""}
                    placeholder="Enter total investment amount"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />
                  {errors.total_investment && (
                    <p className="text-sm text-red-500">
                      {errors.total_investment.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="total_loan_applied">
                    Total Loan Applied (₹)
                  </Label>
                  <Input
                    id="total_loan_applied"
                    {...register("total_loan_applied")}
                    className={
                      errors.total_loan_applied ? "border-red-500" : ""
                    }
                    placeholder="Enter total loan applied amount"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />
                  {errors.total_loan_applied && (
                    <p className="text-sm text-red-500">
                      {errors.total_loan_applied.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="total_loan_released">
                    Total Loan Released (₹)
                  </Label>
                  <Input
                    id="total_loan_released"
                    {...register("total_loan_released")}
                    placeholder="Enter total loan released amount"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />
                  {errors.total_loan_released && (
                    <p className="text-sm text-red-500">
                      {errors.total_loan_released.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="loan_release_date">Loan Release Date</Label>
                  <Input
                    id="loan_release_date"
                    type="date"
                    {...register("loan_release_date")}
                  />
                  {errors.loan_release_date && (
                    <p className="text-sm text-red-500">
                      {errors.loan_release_date.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="loan_period">Loan Period</Label>
                  <Input
                    id="loan_period"
                    {...register("loan_period")}
                    placeholder="e.g., 5 years"
                  />
                  {errors.loan_period && (
                    <p className="text-sm text-red-500">
                      {errors.loan_period.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="promoters_contribution" required>
                    Applicant Contribution (₹)
                  </Label>
                  <Input
                    id="promoters_contribution"
                    {...register("promoters_contribution")}
                    placeholder="Enter promoters contribution amount"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />
                  {errors.promoters_contribution && (
                    <p className="text-sm text-red-500">
                      {errors.promoters_contribution.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="promoters_contribution_percent" required>
                    Applicant Contribution (%)
                  </Label>
                  <Input
                    id="promoters_contribution_percent"
                    {...register("promoters_contribution_percent")}
                    placeholder="Enter percentage"
                    onInput={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (
                        value === "" ||
                        (Number(value) >= 0 && Number(value) <= 100)
                      ) {
                        setValue("promoters_contribution_percent", value);
                      }
                    }}
                  />
                  {errors.promoters_contribution_percent && (
                    <p className="text-sm text-red-500">
                      {errors.promoters_contribution_percent.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="shared_with_other_banks" required>
                    Shared with Other Banks
                  </Label>
                  <Select
                    value={formData.shared_with_other_banks || ""}
                    onValueChange={(value) =>
                      setValue("shared_with_other_banks", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select Yes or No" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Yes">Yes</SelectItem>
                      <SelectItem value="No">No</SelectItem>
                    </SelectContent>
                  </Select>
                  {errors.shared_with_other_banks && (
                    <p className="text-sm text-red-500">
                      {errors.shared_with_other_banks.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="loan_sanction_date">Loan Sanction Date</Label>
                  <Input
                    id="loan_sanction_date"
                    type="date"
                    {...register("loan_sanction_date")}
                  />
                  {errors.loan_sanction_date && (
                    <p className="text-sm text-red-500">
                      {errors.loan_sanction_date.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="loan_application_date">
                    Loan Application Date
                  </Label>
                  <Input
                    id="loan_application_date"
                    type="date"
                    {...register("loan_application_date")}
                  />
                  {errors.loan_application_date && (
                    <p className="text-sm text-red-500">
                      {errors.loan_application_date.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="loan_sanction_by_authority_date">
                    Loan Sanction by Authority Date
                  </Label>
                  <Input
                    id="loan_sanction_by_authority_date"
                    type="date"
                    {...register("loan_sanction_by_authority_date")}
                  />
                  {errors.loan_sanction_by_authority_date && (
                    <p className="text-sm text-red-500">
                      {errors.loan_sanction_by_authority_date.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="repayment_start_date">
                    Repayment Start Date
                  </Label>
                  <Input
                    id="repayment_start_date"
                    type="date"
                    {...register("repayment_start_date")}
                  />
                  {errors.repayment_start_date && (
                    <p className="text-sm text-red-500">
                      {errors.repayment_start_date.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="term_loan_account_number">
                    Term Loan Account Number
                  </Label>
                  <Input
                    id="term_loan_account_number"
                    {...register("term_loan_account_number")}
                    placeholder="Enter account number"
                  />
                  {errors.term_loan_account_number && (
                    <p className="text-sm text-red-500">
                      {errors.term_loan_account_number.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="interest_rate">Interest Rate (%)</Label>
                  <Input
                    id="interest_rate"
                    {...register("interest_rate")}
                    placeholder="Enter interest rate"
                    onInput={(e) => {
                      const value = e.target.value.replace(/\D/g, "");
                      if (
                        value === "" ||
                        (Number(value) >= 0 && Number(value) <= 100)
                      ) {
                        setValue("interest_rate", value);
                      }
                    }}
                  />
                  {errors.interest_rate && (
                    <p className="text-sm text-red-500">
                      {errors.interest_rate.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="interest_charged_per_year">
                    Interest Charged Per Year (₹)
                  </Label>
                  <Input
                    id="interest_charged_per_year"
                    {...register("interest_charged_per_year")}
                    placeholder="Enter interest amount"
                    onInput={(e) => {
                      e.target.value = e.target.value.replace(/\D/g, "");
                    }}
                  />
                  {errors.interest_charged_per_year && (
                    <p className="text-sm text-red-500">
                      {errors.interest_charged_per_year.message}
                    </p>
                  )}
                </div>
              </div>

              <Separator className="my-6" />

              <h3 className="text-lg font-semibold mb-4">Document Uploads</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dpr_copy">Detailed Project Report Copy</Label>
                  <div className="border-2 border-dashed rounded-lg p-4">
                    <Input
                      id="dpr_copy"
                      type="file"
                      accept=".jpeg,.jpg,.png,.pdf"
                      onChange={(e) =>
                        handleFileUpload("dpr_copy", e.target.files[0])
                      }
                      className={errors.dpr_copy ? "border-red-500" : ""}
                    />
                    <FilePreview
                      fieldName="dpr_copy"
                      preview={filePreviews.dpr_copy}
                      file={
                        formData.dpr_copy instanceof File
                          ? formData.dpr_copy
                          : null
                      }
                      existingFile={
                        typeof filePreviews.dpr_copy === "string"
                          ? filePreviews.dpr_copy
                          : null
                      }
                      onRemove={() => handleRemoveFile("dpr_copy")}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported: PDF, JPG, JPEG, PNG (max 5MB)
                    </p>
                  </div>
                  {errors.dpr_copy && (
                    <p className="text-sm text-red-500">
                      {errors.dpr_copy.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="purchase_detail_bond">
                    Undertaking certificate for not selling the loom for 5
                    years. <span className="text-red-500">*</span>
                    <Button
                      type="button"
                      variant="link"
                      onClick={() =>
                        handleDownload(
                          purchase_certificate,
                          `purchase_certificate.pdf`
                        )
                      }
                      className="text-wrap text-blue-500"
                    >
                      (Download Undertaking Certificate <br /> and print in 20
                      rupees stamp paper )
                    </Button>
                  </Label>
                  <div className="border-2 border-dashed rounded-lg p-4">
                    <Input
                      id="purchase_detail_bond"
                      type="file"
                      accept=".jpeg,.jpg,.png,.pdf"
                      onChange={(e) =>
                        handleFileUpload(
                          "purchase_detail_bond",
                          e.target.files[0]
                        )
                      }
                      className={
                        errors.purchase_detail_bond ? "border-red-500" : ""
                      }
                    />
                    <FilePreview
                      fieldName="purchase_detail_bond"
                      preview={filePreviews.purchase_detail_bond}
                      file={
                        formData.purchase_detail_bond instanceof File
                          ? formData.purchase_detail_bond
                          : null
                      }
                      existingFile={
                        typeof filePreviews.purchase_detail_bond === "string"
                          ? filePreviews.purchase_detail_bond
                          : null
                      }
                      onRemove={() => handleRemoveFile("purchase_detail_bond")}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported: PDF, JPG, JPEG, PNG (max 5MB)
                    </p>
                  </div>
                  {errors.purchase_detail_bond && (
                    <p className="text-sm text-red-500">
                      {errors.purchase_detail_bond.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="purchase_proof" required>
                    Purchase Proof
                  </Label>
                  <div className="border-2 border-dashed rounded-lg p-4">
                    <Input
                      id="purchase_proof"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) =>
                        handleFileUpload("purchase_proof", e.target.files[0])
                      }
                    />
                    <FilePreview
                      fieldName="purchase_proof"
                      preview={filePreviews.purchase_proof}
                      file={
                        formData.purchase_proof instanceof File
                          ? formData.purchase_proof
                          : null
                      }
                      existingFile={
                        typeof filePreviews.purchase_proof === "string"
                          ? filePreviews.purchase_proof
                          : null
                      }
                      onRemove={() => handleRemoveFile("purchase_proof")}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported: PDF, JPG, JPEG, PNG (max 2MB)
                    </p>
                    {errors.purchase_proof && (
                      <p className="text-sm text-red-500">
                        {errors.purchase_proof.message}
                      </p>
                    )}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bill_of_lading">Bill of Lading</Label>
                  <div className="border-2 border-dashed rounded-lg p-4">
                    <Input
                      id="bill_of_lading"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) =>
                        handleFileUpload("bill_of_lading", e.target.files[0])
                      }
                    />
                    <FilePreview
                      fieldName="bill_of_lading"
                      preview={filePreviews.bill_of_lading}
                      file={
                        formData.bill_of_lading instanceof File
                          ? formData.bill_of_lading
                          : null
                      }
                      existingFile={
                        typeof filePreviews.bill_of_lading === "string"
                          ? filePreviews.bill_of_lading
                          : null
                      }
                      onRemove={() => handleRemoveFile("bill_of_lading")}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported: PDF, JPG, JPEG, PNG (max 2MB)
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bill_of_entry">Bill of Entry</Label>
                  <div className="border-2 border-dashed rounded-lg p-4">
                    <Input
                      id="bill_of_entry"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) =>
                        handleFileUpload("bill_of_entry", e.target.files[0])
                      }
                    />
                    <FilePreview
                      fieldName="bill_of_entry"
                      preview={filePreviews.bill_of_entry}
                      file={
                        formData.bill_of_entry instanceof File
                          ? formData.bill_of_entry
                          : null
                      }
                      existingFile={
                        typeof filePreviews.bill_of_entry === "string"
                          ? filePreviews.bill_of_entry
                          : null
                      }
                      onRemove={() => handleRemoveFile("bill_of_entry")}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported: PDF, JPG, JPEG, PNG (max 2MB)
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="investment_proof" required>
                    Investment Proof
                  </Label>
                  <div className="border-2 border-dashed rounded-lg p-4">
                    <Input
                      id="investment_proof"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) =>
                        handleFileUpload("investment_proof", e.target.files[0])
                      }
                    />
                    <FilePreview
                      fieldName="investment_proof"
                      preview={filePreviews.investment_proof}
                      file={
                        formData.investment_proof instanceof File
                          ? formData.investment_proof
                          : null
                      }
                      existingFile={
                        typeof filePreviews.investment_proof === "string"
                          ? filePreviews.investment_proof
                          : null
                      }
                      onRemove={() => handleRemoveFile("investment_proof")}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported: PDF, JPG, JPEG, PNG (max 2MB)
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="loan_account_copy">Loan Account Copy</Label>
                  <div className="border-2 border-dashed rounded-lg p-4">
                    <Input
                      id="loan_account_copy"
                      type="file"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) =>
                        handleFileUpload("loan_account_copy", e.target.files[0])
                      }
                    />
                    <FilePreview
                      fieldName="loan_account_copy"
                      preview={filePreviews.loan_account_copy}
                      file={
                        formData.loan_account_copy instanceof File
                          ? formData.loan_account_copy
                          : null
                      }
                      existingFile={
                        typeof filePreviews.loan_account_copy === "string"
                          ? filePreviews.loan_account_copy
                          : null
                      }
                      onRemove={() => handleRemoveFile("loan_account_copy")}
                    />
                    <p className="text-xs text-muted-foreground mt-2">
                      Supported: PDF, JPG, JPEG, PNG (max 2MB)
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}

          <div className="flex justify-end mt-6 gap-2">
            <Button
              type="submit"
              disabled={isSubmitting}
              onClick={() => setButtonType("submit")}
              // onClick={(e) => onSubmitClick(e, "submit")}
              className="min-w-[150px]"
            >
              {isSubmitting && buttonType === "submit" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
            <Button
              type="submit"
              onClick={(e) => setButtonType("next")}
              disabled={isSubmitting}
              className="min-w-[150px]"
            >
              {isSubmitting && buttonType === "next" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Saving...
                </>
              ) : (
                "Save and Next"
              )}
            </Button>
          </div>
        </CardContent>
      </Card>
    </form>
  );
};

export default NewPurchasedDetails;
